import { useMemo, useState } from 'react'

import type { FilaCartera } from '@/lib/supabase/tipos-bd'
import { hoy } from '@/modules/tablero/semana'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Cargando } from '@/shared/ui/Estados/Cargando'

import { AGRUPACIONES, agruparCuentas, type Agrupar } from './agrupar'
import { GrupoDeCuentas } from './GrupoDeCuentas'
import { MatrizClientesEdades } from './MatrizClientesEdades'
import matriz from './MatrizClientesEdades.module.css'
import { debePasarAFev, edadDe, estadoDe, type ClaveEdad, type EstadoCuenta } from './edades'
import { diaEnCartago } from './periodo'
import { PorCliente } from './PorCliente'
import { ResumenCartera } from './ResumenCartera'
import { consultaDe, nombreDeConsulta, SelectorDePeriodo, type Consulta } from './SelectorDePeriodo'
import { TarjetaDeCuenta, type Panel } from './TarjetaDeCuenta'
import { useCartera, usePagosDelPeriodo } from './useCartera'
import estilos from './PaginaCartera.module.css'

/**
 * FINANCIERA → CARTERA (0246).
 *
 * Johan: «cuando un servicio pasa a finalizado, entra a cartera». Aquí no se
 * crea nada: cada servicio del archivo de finalizados ya es una cuenta por
 * cobrar, por lo aprobado de su obra. Lo que se hace es anotar los pagos.
 *
 * Era una tabla y Johan pidió que fuera «más didáctico, versátil»:
 *   · DIDÁCTICO: cada cuenta es una tarjeta que se explica sola —cuánto
 *     falta, la barra de lo pagado, su edad, en qué paso va—.
 *   · VERSÁTIL: se filtra por estado (con su cuenta) y por edad (tocando la
 *     leyenda de arriba), se ordena como se necesite, y se agrupa por cliente
 *     cuando la pregunta es «¿cuánto nos debe este cliente?».
 *
 * Y POR PERIODO (Johan: «debería poder ordenarlo por semanas» y «debería
 * tener el histórico donde me aparezcan todas las cards del periodo que yo
 * seleccione»). Semana, mes o año, como en el tablero:
 *   · lo recaudado de arriba es el del periodo, en bancos y en efectivo;
 *   · la vista «Histórico» muestra todas las cuentas cuyo servicio se
 *     finalizó en ese periodo, pagadas o no, cada una con sus documentos.
 *   Lo que se debe HOY no depende del periodo: una deuda vieja sigue siendo
 *   deuda aunque se mire otra semana.
 *
 * Y DESDE 0251: «solo pasan a cartera FEV» —el valor de cada cuenta es la
 * suma de sus facturas de venta—; la consulta puede ser un día, un rango o
 * un periodo redondo; la vista «Por cliente» dice la cartera que queda de
 * cada uno; y las cifras son las mismas del informe del tablero, porque las
 * dos pantallas leen la misma función de la base.
 */
type Filtro = 'por_cobrar' | 'pasar_a_fev' | 'pagadas' | 'todas'
type Vista = 'matriz' | 'cuentas' | 'historico' | 'clientes'
type Orden = 'vieja' | 'saldo' | 'cliente'

const FILTROS: [Filtro, string][] = [
  ['por_cobrar', 'Por cobrar'],
  ['pasar_a_fev', 'Por pasar a FEV'],
  ['pagadas', 'Pagadas'],
  ['todas', 'Todas'],
]

const ORDENES: [Orden, string][] = [
  ['vieja', 'Más vieja primero'],
  ['saldo', 'Mayor saldo'],
  ['cliente', 'Cliente (A–Z)'],
]

const DEL_FILTRO: Record<Exclude<Filtro, 'pasar_a_fev'>, EstadoCuenta[]> = {
  por_cobrar: ['por_cobrar', 'abonada'],
  pagadas: ['pagada'],
  todas: ['por_cobrar', 'abonada', 'sin_documentos', 'pagada'],
}

// «Por pasar a FEV» no es un estado del cobro sino una alerta: cruza los
// demás (una cuenta abonada puede tener todavía una COT sin facturar).
const cumple = (c: FilaCartera, f: Filtro) =>
  f === 'pasar_a_fev' ? debePasarAFev(c) : DEL_FILTRO[f].includes(estadoDe(c))

const ordenar: Record<Orden, (a: FilaCartera, b: FilaCartera) => number> = {
  vieja: (a, b) => b.dias - a.dias,
  saldo: (a, b) => Number(b.saldo) - Number(a.saldo),
  cliente: (a, b) => (a.cliente ?? '').localeCompare(b.cliente ?? ''),
}

export function PaginaCartera() {
  const { data: cuentas = [], isLoading, error } = useCartera()
  // 0316: de entrada, clientes × edades (Johan: «se debe mostrar clientes en
  // la columna izquierda y edades en la primera fila, y cruzar las ODT»).
  const [vista, setVista] = useState<Vista>('matriz')
  const [enVentana, setEnVentana] = useState<FilaCartera | null>(null)
  const [consulta, setConsulta] = useState<Consulta>(() => consultaDe('semana', hoy()))
  const { desde, hasta } = consulta
  const nombrePeriodo = nombreDeConsulta(consulta)
  // El selector de periodo solo vive en «Histórico» (Johan: «ya no
  // necesitaría el selector de arriba»): lo que se debe es de hoy. Fuera de
  // ahí, lo recaudado es el de la semana en curso.
  const semana = useMemo(() => consultaDe('semana', hoy()), [])
  const delRecaudo = vista === 'historico' ? consulta : semana
  const { data: recaudo } = usePagosDelPeriodo(delRecaudo.desde, delRecaudo.hasta)
  // Todas: con pocas cuentas, empezar filtrado escondía las sin facturar.
  const [filtro, setFiltro] = useState<Filtro>('todas')
  const [orden, setOrden] = useState<Orden>('vieja')
  // Las tarjetas se pueden agrupar por edad, cliente, mes o servicio.
  // «Orden» = sin agrupar, como venían.
  const [agrupar, setAgrupar] = useState<Agrupar>('orden')
  const [edad, setEdad] = useState<ClaveEdad | null>(null)
  const [buscar, setBuscar] = useState('')
  // Una sola tarjeta abierta a la vez, con lo que muestra.
  const [abierta, setAbierta] = useState<{ id: string; panel: Panel } | null>(null)

  // Las del histórico: finalizadas en el periodo, pagadas o no.
  const delPeriodo = useMemo(
    () =>
      cuentas.filter((c) => {
        const d = diaEnCartago(c.finalizado_en)
        return d >= desde && d <= hasta
      }),
    [cuentas, desde, hasta],
  )

  const visibles = useMemo(() => {
    const t = buscar.trim().toLowerCase()
    const base = vista === 'historico' ? delPeriodo : cuentas.filter((c) => cumple(c, filtro))
    return base
      .filter((c) => !edad || (Number(c.saldo) > 0 && edadDe(c.dias).clave === edad))
      .filter((c) => !t || `${c.numero} ${c.cliente ?? ''}`.toLowerCase().includes(t))
      .sort(ordenar[orden])
  }, [cuentas, delPeriodo, vista, filtro, edad, buscar, orden])

  // Las secciones de tarjetas (agrupar.ts). Dentro de cada una, el orden
  // que se eligió.
  const grupos = useMemo(() => agruparCuentas(visibles, agrupar), [visibles, agrupar])

  if (isLoading) return <Cargando mensaje="BALANCEANDO CARTERA PENDIENTE..." />
  if (error) return <Aviso tipo="error">{(error as Error).message}</Aviso>

  const tarjeta = (c: FilaCartera) => (
    <TarjetaDeCuenta
      key={c.orden_servicio_id}
      cuenta={c}
      sinCliente={agrupar === 'cliente'}
      panel={abierta?.id === c.orden_servicio_id ? abierta.panel : null}
      onPanel={(panel) => setAbierta(panel ? { id: c.orden_servicio_id, panel } : null)}
    />
  )

  return (
    <div className={estilos.pagina}>
      <ResumenCartera
        cuentas={cuentas}
        recaudo={recaudo ?? null}
        nombrePeriodo={nombreDeConsulta(delRecaudo)}
        edad={edad}
        onEdad={setEdad}
      />

      {/* ── Las dos maneras de mirar: lo que se debe, o lo del periodo ─── */}
      <div className={estilos.vistas} role="tablist" aria-label="Qué mirar">
        <button
          type="button"
          role="tab"
          aria-selected={vista === 'matriz'}
          className={vista === 'matriz' ? estilos.vistaViva : undefined}
          onClick={() => setVista('matriz')}
        >
          Clientes × edades
          <small>cada orden en su edad</small>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={vista === 'cuentas'}
          className={vista === 'cuentas' ? estilos.vistaViva : undefined}
          onClick={() => setVista('cuentas')}
        >
          Cuentas
          <small>por estado, hoy</small>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={vista === 'historico'}
          className={vista === 'historico' ? estilos.vistaViva : undefined}
          onClick={() => setVista('historico')}
        >
          Histórico · {nombrePeriodo}
          <small>
            {delPeriodo.length}{' '}
            {delPeriodo.length === 1 ? 'servicio finalizado' : 'servicios finalizados'}
          </small>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={vista === 'clientes'}
          className={vista === 'clientes' ? estilos.vistaViva : undefined}
          onClick={() => setVista('clientes')}
        >
          Por cliente
          <small>lo que queda de cada uno</small>
        </button>
        {/* «Asócialo bien con el informe» (Johan): las mismas cuentas. */}
        <p className={estilos.mismasCifras}>Las mismas cifras que el informe del tablero</p>
      </div>

      {vista === 'matriz' ? (
        <>
          <div className={estilos.controles}>
            <input
              className={estilos.buscador}
              type="search"
              placeholder="Cliente, OS u OT…"
              value={buscar}
              onChange={(e) => setBuscar(e.target.value)}
              aria-label="Buscar en la cartera"
            />
          </div>
          <MatrizClientesEdades
            cuentas={cuentas.filter((c) => {
              const t = buscar.trim().toLowerCase()
              return !t || `${c.numero} ${c.orden_trabajo ?? ''} ${c.cliente ?? ''}`.toLowerCase().includes(t)
            })}
            edad={edad}
            onAbrir={(c) => {
              setEnVentana(c)
              setAbierta(null)
            }}
          />
          {enVentana && (
            <div
              className={matriz.velo}
              role="dialog"
              aria-modal="true"
              aria-label={`Cuenta ${enVentana.numero}`}
              onClick={(e) => e.target === e.currentTarget && setEnVentana(null)}
              onKeyDown={(e) => e.key === 'Escape' && setEnVentana(null)}
            >
              <div className={matriz.ventana}>
                <button type="button" className={matriz.cerrar} onClick={() => setEnVentana(null)} autoFocus>
                  Cerrar ×
                </button>
                {tarjeta(cuentas.find((c) => c.orden_servicio_id === enVentana.orden_servicio_id) ?? enVentana)}
              </div>
            </div>
          )}
        </>
      ) : vista === 'clientes' ? (
        <PorCliente
          cuentas={cuentas}
          onCliente={(cliente) => {
            setBuscar(cliente)
            setFiltro('todas')
            setVista('cuentas')
          }}
        />
      ) : (
        <>
          {vista === 'historico' && <SelectorDePeriodo consulta={consulta} onConsulta={setConsulta} />}

          {/* ── Cómo se quiere ver ────────────────────────────────────────── */}
          <div className={estilos.controles}>
            {vista === 'cuentas' && (
              <div className={estilos.filtros} role="group" aria-label="Qué cuentas ver">
                {FILTROS.map(([valor, texto]) => (
                  <button
                    key={valor}
                    type="button"
                    className={filtro === valor ? estilos.filtroVivo : undefined}
                    aria-pressed={filtro === valor}
                    onClick={() => setFiltro(valor)}
                  >
                    {texto}
                    <span className={estilos.cuentaFiltro}>
                      {cuentas.filter((c) => cumple(c, valor)).length}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <label className={estilos.ordenar}>
              <span>Ordenar</span>
              <select value={orden} onChange={(e) => setOrden(e.target.value as Orden)}>
                {ORDENES.map(([valor, texto]) => (
                  <option key={valor} value={valor}>
                    {texto}
                  </option>
                ))}
              </select>
            </label>

            <label className={estilos.ordenar}>
              <span>Agrupar por</span>
              <select value={agrupar} onChange={(e) => setAgrupar(e.target.value as Agrupar)}>
                {AGRUPACIONES.map(([valor, texto]) => (
                  <option key={valor} value={valor}>
                    {texto}
                  </option>
                ))}
              </select>
            </label>

            <input
              className={estilos.buscador}
              type="search"
              placeholder="Cliente u OS-…"
              value={buscar}
              onChange={(e) => setBuscar(e.target.value)}
              aria-label="Buscar en la cartera"
            />
          </div>

          {/* ── Las cuentas ───────────────────────────────────────────────── */}
          {visibles.length === 0 ? (
            <p className={estilos.vacio}>
              {vista === 'historico'
                ? `No se finalizó ningún servicio en ${nombrePeriodo}. Prueba otro periodo o un rango.`
                : cuentas.length === 0
                ? 'Todavía no hay servicios finalizados: la cartera se llena sola cuando uno se finaliza.'
                : 'Nada con ese filtro.'}
            </p>
          ) : grupos ? (
            grupos.map((g) => (
              <GrupoDeCuentas key={g.clave} grupo={g}>
                {g.filas.map(tarjeta)}
              </GrupoDeCuentas>
            ))
          ) : (
            <div className={estilos.cuentas}>{visibles.map(tarjeta)}</div>
          )}
        </>
      )}
    </div>
  )
}
